import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Fragment, useCallback, useState } from 'react';

import { campaignsApi, queryKeys } from '../../lib/api/resources';
import type { AdvertiserGroup, Campaign, CampaignStatus } from '../../lib/api/types';
import { count, date, money, shortId } from '../../lib/format';
import { useToast } from '../../app/toast';
import {
  AdvertiserStatusBadge,
  BudgetBar,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Select,
  StatusBadge,
  Table,
  TableSkeleton,
  Td,
  Th,
} from '../../components/ui';

const STATUS_LABELS: Record<CampaignStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activa',
  PAUSED: 'Pausada',
  FINISHED: 'Finalizada',
  BUDGET_EXHAUSTED: 'Presupuesto agotado',
};

/** Advertisers the operator has collapsed, remembered across visits (ADS-0048). */
const COLLAPSED_KEY = 'campaigns.collapsedAdvertisers';

function readCollapsed(): Set<string> {
  try {
    const stored = window.localStorage.getItem(COLLAPSED_KEY);
    return new Set(stored ? (JSON.parse(stored) as string[]) : []);
  } catch {
    // A corrupt or unavailable store must not take the screen down with it: the cost of failing
    // here is that groups start expanded, which is the default anyway.
    return new Set();
  }
}

/**
 * Every campaign, grouped by advertiser and then by placement, with the controls to run them.
 *
 * <p>The grouping is the server's (ADS-0048): it pages over advertisers rather than campaigns, so a
 * group is never split across two pages, and it returns placements in the order the enum declares
 * them. This screen renders that order rather than sorting again — re-sorting here is how the same
 * four placements end up appearing in two different orders in one panel.
 *
 * <p>Inline status actions are offered from {@code allowedTransitions}, which the backend computes.
 * The panel never decides what is legal — it would be a second copy of the state machine, and the
 * two would drift.
 */
export function CampaignListPage() {
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [page, setPage] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);

  // Ten advertisers a page, not twenty campaigns: the unit of pagination is the group.
  const params = { status: status || undefined, page, size: 10 };
  const groups = useQuery({
    queryKey: queryKeys.campaignsGrouped(params),
    queryFn: () => campaignsApi.listGrouped(params),
  });

  const toggle = useCallback((advertiserId: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(advertiserId)) {
        next.add(advertiserId);
      }
      try {
        window.localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // Losing the preference is not worth failing the click over.
      }
      return next;
    });
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-headline-lg text-text-primary">Campañas</h1>
        <div className="flex items-center gap-3">
          <Select
            aria-label="Filtrar por estado"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as CampaignStatus | '');
              setPage(0);
            }}
            className="w-52"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Link to="/campaigns/new">
            <Button variant="primary">Nueva campaña</Button>
          </Link>
        </div>
      </header>

      <Card className="p-0">
        {groups.isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={6} columns={6} />
          </div>
        ) : groups.isError ? (
          <div className="p-6">
            <ErrorState
              message={(groups.error as Error).message}
              onRetry={() => void groups.refetch()}
            />
          </div>
        ) : groups.data!.content.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title={status ? 'Ninguna campaña con ese estado' : 'Todavía no hay campañas'}
              description={
                status
                  ? 'Prueba con otro filtro para ver el resto.'
                  : 'Crea una campaña y súbele una creatividad para poder lanzarla.'
              }
              action={
                !status && (
                  <Link to="/campaigns/new">
                    <Button variant="primary">Nueva campaña</Button>
                  </Link>
                )
              }
            />
          </div>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Campaña</Th>
                  {/* Where "Anunciante" used to be. Inside a group every row would repeat the name
                      already in the header above it; the pricing model is what actually varies. */}
                  <Th>Modelo</Th>
                  <Th>Estado</Th>
                  <Th numeric>Presupuesto</Th>
                  <Th>Vuelo</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              {groups.data!.content.map((group) => (
                <AdvertiserSection
                  key={group.advertiserId}
                  group={group}
                  collapsed={collapsed.has(group.advertiserId)}
                  onToggle={toggle}
                />
              ))}
            </Table>

            <Pagination
              page={groups.data!.number}
              totalPages={groups.data!.totalPages}
              totalElements={groups.data!.totalElements}
              onChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * One advertiser's campaigns, as its own {@code tbody}.
 *
 * <p>A {@code tbody} per advertiser rather than one for the whole table: it is what lets the header
 * row, the placement headings, and the campaigns stay one unit, and it keeps the group legible to
 * assistive technology instead of being an undifferentiated run of rows.
 */
function AdvertiserSection({
  group,
  collapsed,
  onToggle,
}: {
  group: AdvertiserGroup;
  collapsed: boolean;
  onToggle: (advertiserId: string) => void;
}) {
  const headingId = `advertiser-${group.advertiserId}`;

  return (
    <tbody className="border-t border-surface-tertiary">
      <tr className="bg-surface-secondary/60">
        <td colSpan={6} className="px-4 py-2">
          <button
            type="button"
            id={headingId}
            aria-expanded={!collapsed}
            onClick={() => onToggle(group.advertiserId)}
            className="flex w-full items-center gap-3 text-left"
          >
            <span
              aria-hidden="true"
              className="font-mono text-label-md text-text-secondary transition-transform"
            >
              {collapsed ? '▸' : '▾'}
            </span>
            <span className="text-body-lg text-text-primary">
              {group.advertiserName ?? 'Anunciante desconocido'}
            </span>
            {group.advertiserStatus && (
              <AdvertiserStatusBadge status={group.advertiserStatus} />
            )}
            {/* The count is the server's, computed after the status filter, so it always matches
                the rows underneath rather than the advertiser's lifetime total. */}
            <span className="ml-auto font-mono text-label-md text-text-secondary">
              {count(group.campaignCount)}{' '}
              {group.campaignCount === 1 ? 'campaña' : 'campañas'}
            </span>
          </button>
        </td>
      </tr>

      {!collapsed &&
        group.placements.map((placementGroup) => (
          <Fragment key={placementGroup.placement}>
            <tr>
              <td
                colSpan={6}
                className="px-4 pb-1 pt-3 font-mono text-label-sm uppercase tracking-wide text-text-secondary"
              >
                {placementGroup.placement}
                <span className="ml-2 normal-case">({placementGroup.campaigns.length})</span>
              </td>
            </tr>
            {placementGroup.campaigns.map((campaign) => (
              <CampaignRow key={campaign.id} campaign={campaign} />
            ))}
          </Fragment>
        ))}
    </tbody>
  );
}

function CampaignRow({ campaign }: { campaign: Campaign }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const changeStatus = useMutation({
    mutationFn: (target: CampaignStatus) => campaignsApi.changeStatus(campaign.id, target),
    onSuccess: (updated) => {
      toast.success(`«${updated.name}» ahora está ${STATUS_LABELS[updated.status].toLowerCase()}`);
      // Scoped invalidation: lists and this campaign's detail, nothing else.
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.campaign(campaign.id) });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () => campaignsApi.remove(campaign.id),
    onSuccess: () => {
      toast.success(`«${campaign.name}» eliminada`);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['advertisers'] });
    },
    // Surfaced verbatim: the server explains that the campaign has history and to finish it
    // instead. A generic "no se pudo eliminar" would leave the operator without a next step.
    onError: (error: Error) => toast.error(error.message),
  });

  /**
   * Deleting is offered only for a DRAFT campaign (ADS-0047).
   *
   * A campaign that has served almost certainly has events, and the server refuses those. Showing
   * a button that reliably fails teaches an operator to distrust the panel; DRAFT is the state
   * where deleting is what they actually want — undoing a row created by mistake.
   */
  const deletable = campaign.status === 'DRAFT';

  /**
   * Only transitions an operator would perform by hand. BUDGET_EXHAUSTED is reached by spending,
   * not by choosing it, so offering it as a button would be offering a way to fake exhaustion.
   */
  const actions = campaign.allowedTransitions.filter(
    (target) => target === 'ACTIVE' || target === 'PAUSED' || target === 'FINISHED',
  );

  return (
    <tr className="hover:bg-surface-tertiary/40">
      <Td>
        <Link to={`/campaigns/${campaign.id}`} className="text-text-primary hover:text-electric-blue">
          {campaign.name}
        </Link>
        {/* The placement is the heading this row sits under, so repeating it here would say the
            same thing twice; the pricing model has moved to its own column. */}
        <p className="font-mono text-label-sm text-text-secondary" title={campaign.id}>
          {shortId(campaign.id)}
        </p>
      </Td>
      <Td>
        <span className="font-mono text-label-md text-text-secondary">
          {campaign.pricingModel}
        </span>
      </Td>
      <Td>
        <StatusBadge status={campaign.status} />
      </Td>
      <Td numeric>
        <span className="whitespace-nowrap">
          {money(campaign.budgetConsumed)} / {money(campaign.budgetTotal)}
        </span>
        <div className="mt-1">
          <BudgetBar consumed={campaign.budgetConsumed} total={campaign.budgetTotal} />
        </div>
      </Td>
      <Td>
        <span className="whitespace-nowrap font-mono text-label-md text-text-secondary">
          {date(campaign.startDate)} → {date(campaign.endDate)}
        </span>
      </Td>
      <Td>
        <div className="flex flex-wrap gap-2">
          {actions.map((target) => (
            <Button
              key={target}
              variant={target === 'FINISHED' ? 'destructive' : 'secondary'}
              loading={changeStatus.isPending}
              onClick={() => {
                // Finishing is terminal and cannot be undone, so it is confirmed by name.
                if (target === 'FINISHED') {
                  const confirmed = window.confirm(
                    `Finalizar «${campaign.name}»? Es irreversible: no podrá volver a servir.`,
                  );
                  if (!confirmed) {
                    return;
                  }
                }
                changeStatus.mutate(target);
              }}
              className="px-2 py-1 text-label-md"
            >
              {target === 'ACTIVE' ? 'Reanudar' : target === 'PAUSED' ? 'Pausar' : 'Finalizar'}
            </Button>
          ))}
          {deletable && (
            <Button
              variant="ghost"
              loading={remove.isPending}
              className="px-2 py-1 text-label-md"
              onClick={() => {
                const confirmed = window.confirm(
                  `Eliminar «${campaign.name}»? No se puede deshacer.`,
                );
                if (confirmed) {
                  remove.mutate();
                }
              }}
            >
              Eliminar
            </Button>
          )}
          {actions.length === 0 && !deletable && (
            <span className="text-label-md text-text-secondary">—</span>
          )}
        </div>
      </Td>
    </tr>
  );
}

function Pagination({
  page,
  totalPages,
  totalElements,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalElements: number;
  onChange: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-surface-tertiary px-4 py-3">
      {/* Anunciantes, not campañas: this list pages over groups, and saying "campañas" here would
          contradict the number the operator can count on screen. */}
      <p className="font-mono text-label-md text-text-secondary">
        {count(totalElements)} {totalElements === 1 ? 'anunciante' : 'anunciantes'} · página{' '}
        {page + 1} de {Math.max(1, totalPages)}
      </p>
      <div className="flex gap-2">
        <Button disabled={page === 0} onClick={() => onChange(page - 1)} className="px-3 py-1">
          Anterior
        </Button>
        <Button
          disabled={page + 1 >= totalPages}
          onClick={() => onChange(page + 1)}
          className="px-3 py-1"
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
